if (!customElements.get('offer-loop-timer')) {
  class OfferLoopTimer extends HTMLElement {
    constructor() {
      super();
      
      this.intervalId = null;
      this.startTimestamp = 0;
      this.durationMilliseconds = 0;
      this.storageKey = '';
    }

    connectedCallback() {
      const sectionId = this.getSectionId();
      const durationSeconds = this.getDurationSeconds();
      this.timeNodes = this.querySelectorAll('.offer-timer__time');

      if (!this.canInitializeTimer(sectionId, durationSeconds)) {
        return;
      }

      this.initializeTimerState(sectionId, durationSeconds);
      this.startTimerLoop();
    }

    getSectionId() {
      return this.getAttribute('data-section-id');
    }

    getDurationSeconds() {
      return Number(this.getAttribute('data-duration-seconds'));
    }

    canInitializeTimer(sectionId, durationSeconds) {
      const hasValidSectionId = Boolean(sectionId);
      const hasValidDuration = Number.isFinite(durationSeconds) && durationSeconds > 0;
      const hasTimeNodes = this.timeNodes.length > 0;

      return hasValidSectionId && hasValidDuration && hasTimeNodes;
    }

    initializeTimerState(sectionId, durationSeconds) {
      this.durationMilliseconds = durationSeconds * 1000;
      this.storageKey = `folqs-offer-loop-timer-${sectionId}-${durationSeconds}`;
      this.startTimestamp = this.resolveStartTimestamp();
    }

    startTimerLoop() {
      this.render();
      this.intervalId = window.setInterval(() => this.render(), 1000);
    }

    disconnectedCallback() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    safeStorageGet(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    safeStorageSet(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        return;
      }
    }

    resolveStartTimestamp() {
      const currentTimestamp = Date.now();
      const storedStartTimestamp = this.getStoredStartTimestamp();

      if (!this.isStoredStartTimestampValid(storedStartTimestamp, currentTimestamp)) {
        this.safeStorageSet(this.storageKey, String(currentTimestamp));
        return currentTimestamp;
      }

      return this.normalizeStartTimestamp(storedStartTimestamp, currentTimestamp);
    }

    getStoredStartTimestamp() {
      const storedStartTimestampRaw = this.safeStorageGet(this.storageKey);
      return Number.parseInt(storedStartTimestampRaw, 10);
    }

    isStoredStartTimestampValid(storedStartTimestamp, currentTimestamp) {
      return Number.isFinite(storedStartTimestamp) && storedStartTimestamp > 0 && storedStartTimestamp <= currentTimestamp;
    }

    normalizeStartTimestamp(storedStartTimestamp, currentTimestamp) {
      const elapsedMilliseconds = currentTimestamp - storedStartTimestamp;
      if (elapsedMilliseconds < this.durationMilliseconds) {
        return storedStartTimestamp;
      }

      const completedCyclesCount = Math.floor(elapsedMilliseconds / this.durationMilliseconds);
      const normalizedStartTimestamp = storedStartTimestamp + completedCyclesCount * this.durationMilliseconds;
      this.safeStorageSet(this.storageKey, String(normalizedStartTimestamp));

      return normalizedStartTimestamp;
    }

    formatTime(totalSeconds) {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const formattedHours = hours < 10 ? `0${hours}` : String(hours);
      const formattedMinutes = minutes < 10 ? `0${minutes}` : String(minutes);
      const formattedSeconds = seconds < 10 ? `0${seconds}` : String(seconds);

      return `${formattedHours} : ${formattedMinutes} : ${formattedSeconds}`;
    }

    render() {
      const currentTimestamp = Date.now();
      const remainingSeconds = this.getRemainingSeconds(currentTimestamp);
      const timeText = this.formatTime(remainingSeconds);
      this.updateTimeNodes(timeText);
    }

    getRemainingSeconds(currentTimestamp) {
      let elapsedMilliseconds = currentTimestamp - this.startTimestamp;

      if (elapsedMilliseconds >= this.durationMilliseconds) {
        const completedCyclesCount = Math.floor(elapsedMilliseconds / this.durationMilliseconds);
        this.startTimestamp += completedCyclesCount * this.durationMilliseconds;
        this.safeStorageSet(this.storageKey, String(this.startTimestamp));
        elapsedMilliseconds = currentTimestamp - this.startTimestamp;
      }

      const remainingMilliseconds = this.durationMilliseconds - elapsedMilliseconds;
      return Math.max(0, Math.ceil(remainingMilliseconds / 1000));
    }

    updateTimeNodes(timeText) {
      this.timeNodes.forEach((node) => {
        node.textContent = timeText;
      });
    }
  }

  customElements.define('offer-loop-timer', OfferLoopTimer);
}
